import { useEffect, useMemo, useRef, useState } from "react";
import { Check, MapPin, Loader2, AlertCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "./ui/popover";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

/**
 * Postnummerfelt med inkrementelt søk i Postnummerregisteret (Bring).
 * - Skriv sifre («546») → forslag; ved 4 kjente sifre fylles poststed automatisk via onPlace().
 * - Skriv stadnamn («uske») → forslag med postnummer; vel eit → både postnummer og poststed vert sette.
 *
 * Props:
 *  value        postnummer (streng) frå forelder
 *  onChange     (postnummer) → void – berre sifre (0–4) vert sende vidare; tekstsøk gir ""
 *  onPlace      ({postnummer, poststed, kommune}) → void – kallast når eit postnummer er funne/valt
 *  limit        maks forslag (default 8)
 */
export const PostalCodeInput = ({ id, value = "", onChange, onPlace, placeholder, className = "", limit = 8, disabled = false, required = false, testId = "postal-code-input", showHint = true }) => {
  const { t } = useLang();
  const [text, setText] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [match, setMatch] = useState(null); // { postnummer, poststed, kommune } for gjeldande 4-sifra kode
  const [unknown, setUnknown] = useState(false);
  const [loading, setLoading] = useState(false);
  // Startverdi (t.d. lagra arrangement) skal berre gi treff-hint – ikkje overskrive poststedet forelderen alt har
  const lastResolved = useRef(value || "");
  const inputRef = useRef(null);
  const listId = useMemo(() => `${id || "postal"}-listbox`, [id]);

  // Forelder endra verdien (t.d. lasta eit arrangement for redigering) – vis treff, men ikkje autofyll poststed
  useEffect(() => {
    const digits = (text || "").replace(/\D/g, "");
    if ((value || "") !== digits && !(/[^\d]/.test(text) && !value)) {
      lastResolved.current = value || "";
      setText(value || "");
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Eksakt oppslag ved 4 sifre → autofyll poststed
  useEffect(() => {
    const digits = (text || "").replace(/\D/g, "");
    if (!/^\d{4}$/.test(text || "")) {
      setMatch(null);
      setUnknown(false);
      return;
    }
    if (lastResolved.current === digits && match?.postnummer === digits) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/postnummer/${digits}`);
        if (cancelled) return;
        setMatch(data);
        setUnknown(false);
        if (lastResolved.current !== digits) {
          lastResolved.current = digits;
          onPlace?.(data);
        }
      } catch {
        if (cancelled) return;
        setMatch(null);
        setUnknown(true);
      }
    })();
    return () => { cancelled = true; };
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inkrementelt søk (debounce 150 ms)
  useEffect(() => {
    const q = (text || "").trim();
    const isDigits = /^\d+$/.test(q);
    if (q.length < 2 || (isDigits && q.length >= 4 && match) || !open) {
      if (q.length < 2) setItems([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/postnummer", { params: { q, limit } });
        if (!cancelled) {
          setItems(data.items || []);
          setActive(0);
        }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [text, open, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (item) => {
    lastResolved.current = item.postnummer;
    setText(item.postnummer);
    setMatch(item);
    setUnknown(false);
    setItems([]);
    setOpen(false);
    onChange?.(item.postnummer);
    onPlace?.(item);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleChange = (e) => {
    let v = e.target.value;
    if (/^\d*$/.test(v)) v = v.slice(0, 4);
    setText(v);
    setOpen(true);
    const digits = v.replace(/\D/g, "");
    onChange?.(/^\d*$/.test(v) ? digits : "");
  };

  const handleKeyDown = (e) => {
    if (!open || items.length === 0) {
      if (e.key === "ArrowDown" && items.length === 0 && text.length >= 2) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); }
    else if (e.key === "Enter") { e.preventDefault(); select(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  // Lista skjules straks det står 4 sifre (då er det berre eksakt oppslag/hint som gjeld)
  const showList = open && items.length > 0 && !/^\d{4}$/.test(text || "") && !(match && text === match.postnummer);

  return (
    <div className={`space-y-1 ${className}`}>
      <Popover open={showList} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              id={id}
              value={text}
              onChange={handleChange}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 120)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || t("5463 eller Uskedalen")}
              autoComplete="off"
              inputMode="text"
              maxLength={40}
              disabled={disabled}
              required={required}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={showList}
              aria-controls={listId}
              aria-activedescendant={showList ? `${listId}-opt-${active}` : undefined}
              className={`pr-8 ${unknown ? "border-[hsl(var(--danger))] focus-visible:ring-[hsl(var(--danger))]" : ""}`}
              data-testid={testId}
            />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : match ? <Check className="w-4 h-4 text-[hsl(var(--success))]" data-testid={`${testId}-match-icon`} /> : <MapPin className="w-4 h-4" />}
            </span>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="p-1 w-max min-w-[var(--radix-popover-trigger-width)] max-w-[min(24rem,calc(100vw-2rem))] max-h-72 overflow-y-auto bg-popover/95 backdrop-blur-md"
          data-testid={`${testId}-suggestions`}
        >
          <ul id={listId} role="listbox" aria-label={t("Postnummer-forslag")}>
            {items.map((it, i) => (
              <li
                key={`${it.postnummer}-${it.poststed}`}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => select(it)}
                className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${i === active ? "bg-secondary" : "hover:bg-secondary/70"}`}
                data-testid={`${testId}-option-${it.postnummer}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono tabular-nums font-medium">{it.postnummer}</span>
                  <span className="truncate">{it.poststed}</span>
                  {it.kategori === "P" && <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1">{t("postboks")}</span>}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">{it.kommune}</span>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
      {showHint && match && (
        <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`${testId}-hint`}>
          <Check className="w-3 h-3 text-[hsl(var(--success))]" /> {match.poststed} · {match.kommune}
        </p>
      )}
      {showHint && unknown && (
        <p className="text-xs text-[hsl(var(--danger))] flex items-center gap-1" data-testid={`${testId}-unknown`}>
          <AlertCircle className="w-3 h-3" /> {t("Ukjent postnummer")}
        </p>
      )}
    </div>
  );
};
